import axios from 'axios';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { BodyWrapper, Header, Loader, MainWrapper } from '@components';
import { businessQuery, locationsQuery, sitesQuery } from '@queries';
import { DateField, MultiSelectField, ToggleField } from '@Forms/fields.jsx';
import { fromLocalDate, toLocalDate } from '@Forms/utils.js';

import { ProjectBreadcrumbs } from '../components/ProjectBreadcrumbs/ProjectBreadcrumbs.jsx';
import ProjectSidebar from '../components/ProjectSidebar/ProjectSidebar.jsx';

export const FormLeads = () => {
  const { t, i18n } = useTranslation();
  const { businessID } = useParams();
  const [isExporting, setIsExporting] = useState(false);

  // Get business
  const { data: businessData, isPending: businessIsPending } = useQuery(
    businessQuery(businessID),
  );

  // Get locations
  const { data: locationsData, isPending: locationsIsPending } = useQuery(
    locationsQuery({
      business_id: businessID,
      limit: 1000,
      order_by: 'location_name',
      _fields: ['id', 'location_name'].join(','),
    }),
  );

  // Get sites for the business
  const { data: sitesData, isPending: sitesIsPending } = useQuery(
    sitesQuery({
      business_id: businessID,
      deleted: 0,
      limit: 1000,
      order_by: 'base_directory',
      type: 'default',
      _fields: ['id', 'display_url', 'has_draft'].join(','),
    }),
  );

  // Get sites for the business
  const { data: allSitesData, isPending: allSitesIsPending } = useQuery(
    sitesQuery({
      business_id: businessID,
      deleted: 0,
      limit: 1000,
      _fields: 'id',
    }),
  );

  // Get ids for all sites in the business
  const siteIds = allSitesData?.objects?.map((site) => site.id) || [];
  const siteIdsSet = new Set(siteIds);
  // Fetch all forms and filter client-side by site_id
  const { data: allFormsData, isPending: formsIsPending } = useQuery({
    queryKey: ['forms', 'list', { deleted: 0 }],
    queryFn: async () => {
      const response = await axios.get('/api/v2/forms/', {
        params: {
          deleted: 0,
          limit: 1000,
          order_by: 'name',
        },
      });
      return response.data;
    },
  });

  // Filter forms to only include those from sites in this business
  const formsData = allFormsData
    ? {
        ...allFormsData,
        objects: allFormsData.objects.filter(
          (form) =>
            !form.next_form_id && siteIdsSet.has(form.site_id || form.site?.id),
        ),
      }
    : null;

  // Calculate default dates: last 30 days
  const today = new Date();
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(today.getDate() - 30);

  const {
    handleSubmit,
    control,
    getValues,
    watch,
    register,
    formState: { errors },
    setValue,
  } = useForm({
    mode: 'onBlur',
    defaultValues: {
      from_date: fromLocalDate(thirtyDaysAgo),
      to_date: fromLocalDate(today),
      locations: [],
      forms: [],
      spam: false,
    },
  });

  // Watch date values for validation
  const fromDate = watch('from_date');
  const toDate = watch('to_date');

  // Consolidated validation rules
  const getValidationRules = (fieldName) => {
    const rules = {
      from_date: {
        required: 'From Date is required',
        validate: (value) => {
          if (!value) return 'From Date is required';
          if (!toDate) return true;
          const from = toLocalDate(value);
          const to = toLocalDate(toDate);
          if (from > to) {
            return 'From Date cannot be after To Date';
          }
          return true;
        },
      },
      to_date: {
        required: 'To Date is required',
        validate: (value) => {
          if (!value) return 'To Date is required';
          if (!fromDate) return true;
          const from = toLocalDate(fromDate);
          const to = toLocalDate(value);
          if (to < from) {
            return 'To Date cannot be before From Date';
          }
          return true;
        },
      },
      locations: {
        // required: 'At least one location must be selected',
      },
      forms: {
        // required: 'At least one form must be selected',
      },
      websites: {
        // required: 'At least one website must be selected',
      },
    };
    return rules[fieldName] || {};
  };

  // Format locations as options for MultiSelectField
  const locationOptions = locationsData?.objects
    ? locationsData.objects.map((location) => ({
        value: location.id,
        label: location.location_name || `Location ${location.id}`,
      }))
    : [];

  // Format forms as options for MultiSelectField
  const formOptions = formsData?.objects
    ? formsData.objects.map((form) => ({
        value: form.id,
        label: `${form.name} (ID: ${form.id})`,
      }))
    : [];

  // Format websites as options for MultiSelectField
  const sitesOptions = sitesData?.objects
    ? sitesData.objects.map((website) => ({
        value: website.id,
        label: website.display_url,
      }))
    : [];

  // Format date to YYYY-MM-DD HH:mm:ss format for API
  const formatTimestamp = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  };

  const spamChange = (e) => {
    setValue('spam', e.target.checked);
  };

  const handleExport = async (data) => {
    try {
      console.log(
        'Exporting Form leads from',
        data.spam,
        data.from_date,
        'to',
        data.to_date,
        'locations:',
        data.locations,
        'forms:',
        data.forms,
        data.websites,
      );
      setIsExporting(true);

      // Format dates for API
      const fromDate = toLocalDate(data.from_date);
      fromDate.setHours(0, 0, 0, 0);
      const toDate = toLocalDate(data.to_date);
      toDate.setHours(23, 59, 59, 999);

      // Build query parameters
      const params = new URLSearchParams({
        timestamp__gt: formatTimestamp(fromDate),
        timestamp__lt: formatTimestamp(toDate),
        ...(data.forms.length > 0 ? { form_id__in: data.forms.join(',') } : {}),
        ...(data.locations.length > 0
          ? { location_id__in: data.locations.join(',') }
          : {}),
        ...(data.spam ? {} : { spam: '0' }),
        ...(data.websites?.length > 0
          ? { site_id__in: data.websites?.join(',') }
          : {}),
      });

      // Construct the download URL
      const downloadUrl = `/api/v2/contact_log/csv_report/?${params.toString()}`;

      // Open in new tab to trigger download
      window.open(downloadUrl, '_blank');

      // Reset exporting state after a short delay
      setTimeout(() => {
        setIsExporting(false);
      }, 1000);
    } catch (error) {
      console.error('Error exporting Form leads', error);
      setIsExporting(false);
    }
  };

  const isPending =
    businessIsPending ||
    locationsIsPending ||
    sitesIsPending ||
    formsIsPending ||
    allSitesIsPending;

  return (
    <>
      <Header />
      <BodyWrapper>
        <ProjectSidebar businessID={businessID} businessData={businessData} />
        <MainWrapper>
          <ProjectBreadcrumbs businessData={businessData}>
            <li className="sb-flex">
              <span>Form leads</span>
            </li>
          </ProjectBreadcrumbs>
          <div className="sb-manage-content">
            <div className="sb-manage-content-head">
              <h1>Form leads</h1>
            </div>
            {isPending ? (
              <Loader className="sb-loader sb-loader-large" />
            ) : (
              <form
                onSubmit={handleSubmit(handleExport)}
                className={
                  `sb-manage-content-form ` +
                  (isExporting ? 'sb-form-disabled' : '')
                }
              >
                <fieldset className="sb-form-item">
                  <div className="sb-form-item-inner">
                    <div className="sb-form-item-elements">
                      <div
                        className="sb-flex"
                        style={{ gap: '24px', flexDirection: 'column' }}
                      >
                        <div className="sb-flex" style={{ gap: '24px' }}>
                          <div style={{ flex: '1' }}>
                            <Controller
                              control={control}
                              name="from_date"
                              id="from_date"
                              rules={getValidationRules('from_date')}
                              render={({ field: controllerField }) => (
                                <DateField
                                  error={errors.from_date}
                                  field={controllerField}
                                  helpText=""
                                  label="From Date"
                                  value={getValues('from_date')}
                                  isClearable={true}
                                />
                              )}
                            />
                          </div>
                          <div style={{ flex: '1' }}>
                            <Controller
                              control={control}
                              name="to_date"
                              id="to_date"
                              rules={getValidationRules('to_date')}
                              render={({ field: controllerField }) => (
                                <DateField
                                  error={errors.to_date}
                                  field={controllerField}
                                  helpText=""
                                  label="To Date"
                                  value={getValues('to_date')}
                                  isClearable={true}
                                />
                              )}
                            />
                          </div>
                        </div>
                        <div>
                          <MultiSelectField
                            control={control}
                            error={errors.locations}
                            name="locations"
                            label="Locations"
                            options={locationOptions}
                            helpText="Select one or more locations to filter form leads. Leave blank for leads from all locations and sites"
                            rules={getValidationRules('locations')}
                          />
                        </div>
                        <div>
                          <MultiSelectField
                            control={control}
                            error={errors.forms}
                            name="forms"
                            label="Forms"
                            options={formOptions}
                            rules={getValidationRules('forms')}
                            helpText="Select one or more forms to filter form leads. Leave blank for leads from all forms"
                          />
                        </div>
                        <div>
                          <MultiSelectField
                            control={control}
                            error={errors.websites}
                            name="websites"
                            label="Websites"
                            options={sitesOptions}
                            rules={getValidationRules('websites')}
                            helpText="Select one or more websites to filter form leads. Leave blank for leads from all websites"
                          />
                        </div>
                        <div>
                          <ToggleField
                            register={register}
                            control={control}
                            error={errors.spam}
                            name="spam"
                            label="Include spam"
                            options={{ onChange: spamChange }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </fieldset>
                <button
                  type="submit"
                  className="sb-btn sb-btn-save"
                  disabled={isExporting || isPending}
                >
                  {isExporting ? 'Exporting...' : 'Export leads (CSV)'}
                </button>
              </form>
            )}
          </div>
        </MainWrapper>
      </BodyWrapper>
    </>
  );
};

export default FormLeads;
